import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from 'cn';
import { Skeleton } from '@/components/ui/skeleton';
import { IconRail } from '@/features/reader/IconRail';
import { ApiError } from '@/lib/http';
import { fetchLanguages } from '@/lib/languages';
import { approveRow, fetchQueue, rejectRow } from './reviewApi';
import { RejectDialog } from './RejectDialog';
import { ReportsTab } from './ReportsTab';
import { ReviewRow } from './ReviewRow';
import {
  headline,
  type QueueSort,
  type QueueTab,
  type RejectCause,
  type ReviewRowData,
} from './reviewTypes';

/**
 * The review queue.
 *
 * A backlog of human sign-off over translations that are **already live for
 * readers** — publishing stopped approving, so nothing here is waiting to go
 * out. That is why the screen leads with how much live content is unreviewed
 * and for how long, rather than with a row count.
 */

const TABS: { id: QueueTab | 'reports'; label: string }[] = [
  { id: 'unreviewed', label: 'Unreviewed' },
  // Reports get their own tab rather than sinking among sign-offs: they are
  // rarer, more urgent, and the only signal from the people this is for.
  { id: 'reports', label: 'Reader reports' },
  { id: 'reviewed', label: 'Reviewed' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'stale', label: 'Stale' },
];

/**
 * One array, not a new `[]` each render.
 *
 * `queue.data?.rows ?? []` looks harmless and is not: a fresh array every
 * render changes the keydown effect's dependencies every render, so the
 * listener is torn down and rebound continuously.
 */
const NO_ROWS: readonly ReviewRowData[] = [];

const SORTS: { id: QueueSort; label: string }[] = [
  { id: 'oldest-live', label: 'Longest live, unreviewed' },
  { id: 'newest', label: 'Newest' },
  { id: 'most-flags', label: 'Most flags' },
  { id: 'khutbah', label: 'By Friday' },
];

export function ReviewQueueScreen() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<QueueTab | 'reports'>('unreviewed');
  const [language, setLanguage] = useState<string | null>(null);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sort, setSort] = useState<QueueSort>('oldest-live');
  const [cursor, setCursor] = useState(0);
  const [rejecting, setRejecting] = useState<ReviewRowData | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const languages = useQuery({
    queryKey: ['languages', 'target'],
    queryFn: ({ signal }) => fetchLanguages('target', signal),
    staleTime: 60 * 60 * 1000,
  });

  const queue = useQuery({
    // Every input that changes the result is in the key, so switching tab
    // cannot serve the previous tab's rows.
    queryKey: ['review', 'queue', tab, language, overdueOnly, sort],
    queryFn: ({ signal }) =>
      fetchQueue(tab as QueueTab, language, overdueOnly, sort, signal),
    enabled: tab !== 'reports',
  });

  const rows = queue.data?.rows ?? NO_ROWS;
  const summary = queue.data?.summary;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['review', 'queue'] });
  }

  const approve = useMutation({
    retry: false,
    mutationFn: (rowId: string) => approveRow(rowId),
    onSuccess: () => {
      setActionError(null);
      refresh();
    },
    onError: (error) => setActionError(message(error)),
  });

  const reject = useMutation({
    retry: false,
    mutationFn: (input: {
      rowId: string;
      causes: RejectCause[];
      note: string;
      keepReadable: boolean;
    }) => rejectRow(input.rowId, input.causes, input.note, input.keepReadable),
    onSuccess: () => {
      setRejecting(null);
      setActionError(null);
      refresh();
    },
    onError: (error) => setActionError(message(error)),
  });

  // J/K move, O opens, A approves, R rejects — and every one of them is also
  // a button on the row, because a shortcut nobody discovers is not a
  // feature. Suppressed while the dialog is open or focus is in a field.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (rejecting !== null || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) {
        return;
      }
      const row = rows[cursor];
      switch (event.key.toLowerCase()) {
        case 'j':
          event.preventDefault();
          setCursor((c) => Math.min(c + 1, Math.max(rows.length - 1, 0)));
          break;
        case 'k':
          event.preventDefault();
          setCursor((c) => Math.max(c - 1, 0));
          break;
        case 'o':
          if (row) open(row);
          break;
        case 'a':
          if (row && !row.reviewed && !row.stale) approve.mutate(row.id);
          break;
        case 'r':
          if (row && !row.rejected) setRejecting(row);
          break;
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [rows, cursor, rejecting, approve]);

  function open(row: ReviewRowData) {
    // The dedicated review view is designed but not built, so Open goes to
    // the editor — the only screen that shows the Arabic beside this
    // language's translation, which is what reviewing needs.
    window.location.search =
      `?screen=translate&khutbah=${encodeURIComponent(row.khutbahId)}`;
  }

  return (
    <div className="flex min-h-full bg-background text-foreground">
      <IconRail current="admin" />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-border bg-card px-4 py-3 sm:px-6">
          <div className="mx-auto w-full max-w-[1200px]">
            <nav aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li>
                  <a href="?screen=upload" className="hover:underline">
                    Admin review
                  </a>
                </li>
                <li aria-hidden>›</li>
                <li className="font-medium text-foreground" aria-current="page">
                  Review queue
                </li>
              </ol>
            </nav>

            <h1 className="mt-2 text-xl font-semibold">Review queue</h1>
            {/* The first line of the screen says what the queue IS: not work
                waiting to go out, but text readers already have. */}
            <p
              aria-live="polite"
              className={cn(
                'mt-1 text-sm',
                summary && summary.overdue > 0
                  ? 'font-medium text-warning-foreground'
                  : 'text-muted-foreground',
              )}
            >
              {summary ? headline(summary) : 'Counting what readers can see…'}
            </p>

            {summary && !summary.faithfulnessCheckOn ? (
              <p className="mt-1 text-xs text-muted-foreground">
                The automatic faithfulness check is not running yet, so every
                translation shows as unchecked. That is the feature, not a backlog.
              </p>
            ) : null}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div role="tablist" aria-label="Queue" className="flex flex-wrap gap-1">
                {TABS.map((entry) => (
                  <button
                    key={entry.id}
                    role="tab"
                    aria-selected={tab === entry.id}
                    onClick={() => {
                      setTab(entry.id);
                      setCursor(0);
                    }}
                    className={cn(
                      'min-h-9 rounded-md px-3 text-sm',
                      'outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
                      tab === entry.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>

              <span className="ms-auto flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs">
                  <span className="text-muted-foreground">Language</span>
                  <select
                    value={language ?? ''}
                    onChange={(event) => setLanguage(event.target.value || null)}
                    className="h-8 rounded-md border border-border bg-background px-2 text-[13px]"
                  >
                    <option value="">All</option>
                    {(languages.data ?? []).map((entry) => (
                      <option key={entry.code} value={entry.code}>
                        {entry.nameEn}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    checked={overdueOnly}
                    onChange={(event) => setOverdueOnly(event.target.checked)}
                  />
                  Overdue only
                </label>

                <label className="flex items-center gap-1.5 text-xs">
                  <span className="text-muted-foreground">Sort</span>
                  <select
                    value={sort}
                    onChange={(event) => setSort(event.target.value as QueueSort)}
                    className="h-8 rounded-md border border-border bg-background px-2 text-[13px]"
                  >
                    {SORTS.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.label}
                      </option>
                    ))}
                  </select>
                </label>
              </span>
            </div>

            <p className="mt-2 text-xs text-muted-foreground">
              J K move · O open · A approve · R reject
            </p>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-[1200px]">
            {actionError ? (
              <p role="alert" className="m-4 rounded-md border border-destructive p-3 text-sm">
                {actionError}
              </p>
            ) : null}

            {tab === 'reports' ? (
              <ReportsTab />
            ) : queue.isPending ? (
              <div className="flex flex-col gap-3 p-4" aria-busy>
                {[0, 1, 2].map((row) => (
                  <Skeleton key={row} className="h-20" />
                ))}
              </div>
            ) : queue.error ? (
              <QueueError error={queue.error} />
            ) : rows.length === 0 ? (
              <Empty tab={tab} />
            ) : (
              <ul>
                {rows.map((row, index) => (
                  <ReviewRow
                    key={row.id}
                    row={row}
                    summary={queue.data.summary}
                    selected={index === cursor}
                    busy={approve.isPending || reject.isPending}
                    onFocus={() => setCursor(index)}
                    onOpen={() => open(row)}
                    onApprove={() => approve.mutate(row.id)}
                    onReject={() => setRejecting(row)}
                  />
                ))}
              </ul>
            )}
          </div>
        </main>
      </div>

      {rejecting ? (
        <RejectDialog
          row={rejecting}
          pending={reject.isPending}
          error={actionError}
          onCancel={() => {
            setRejecting(null);
            setActionError(null);
          }}
          onConfirm={(causes, note, keepReadable) =>
            reject.mutate({ rowId: rejecting.id, causes, note, keepReadable })
          }
        />
      ) : null}
    </div>
  );
}

function message(error: unknown): string {
  if (error instanceof ApiError && error.unauthenticated) {
    return 'Sign in to the editor surface first — a background request cannot raise the browser’s prompt.';
  }
  return error instanceof Error ? error.message : 'Something went wrong.';
}

function QueueError({ error }: { error: unknown }) {
  return (
    <div role="alert" className="m-4 rounded-lg border border-destructive p-4">
      <p className="font-semibold">Could not load the queue</p>
      <p className="mt-1 text-sm text-muted-foreground">{message(error)}</p>
    </div>
  );
}

/**
 * Empty is an achievement on the unreviewed tab and simply a fact on the
 * others, so it does not say the same thing in both.
 */
function Empty({ tab }: { tab: QueueTab }) {
  const copy: Record<QueueTab, { title: string; body: string }> = {
    unreviewed: {
      title: 'Nothing live is unreviewed',
      body: 'Every translation readers can see has been read by a person. This is the state to aim for.',
    },
    reviewed: {
      title: 'Nothing approved yet',
      body: 'Approving a language here is what produces a “Reviewed translation” badge for readers.',
    },
    rejected: {
      title: 'Nothing rejected',
      body: 'Rejected translations stay here with their reason until they are resubmitted.',
    },
    stale: {
      title: 'Nothing stale',
      body: 'A translation goes stale when the publisher replaces the Arabic it was made from.',
    },
  };
  return (
    <div className="m-4 rounded-lg border border-dashed border-border p-8 text-center">
      <p className="font-semibold">{copy[tab].title}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-pretty text-muted-foreground">
        {copy[tab].body}
      </p>
    </div>
  );
}
